
import { useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Calendar, Menu, X, LogIn, UserPlus } from "lucide-react";
import { useAuth } from "@/app/hooks/useAuth";

const WebsiteHeader = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { isAuthenticated } = useAuth();
  
  const isActive = (path: string) => location.pathname === path;
  
  const menuItems = [
    { name: "Home", path: "/" },
    { name: "Features", path: "/features" },
    { name: "Pricing", path: "/pricing" },
    { name: "Contact", path: "/contact" },
  ];
  
  // Function to navigate to app dashboard safely in any environment
  const navigateToApp = () => {
    // Always use React Router navigation in preview environment
    console.log("Navigating to dashboard using React Router");
    navigate("/dashboard");
  };
  
  return (
    <header className="bg-white shadow-sm sticky top-0 z-50">
      <div className="container mx-auto px-4">
        <div className="flex justify-between items-center h-16">
          {/* Logo */}
          <div 
            className="flex items-center cursor-pointer"
            onClick={() => navigate("/")}
          >
            <Calendar className="h-6 w-6 text-blue-600 mr-2" />
            <span className="font-bold text-xl">fyndtyme</span>
          </div>
          
          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center space-x-4">
            {menuItems.map(item => (
              <Button
                key={item.name}
                variant="ghost"
                className={isActive(item.path) ? "text-blue-600" : "text-gray-600"}
                onClick={() => navigate(item.path)}
              >
                {item.name}
              </Button>
            ))}
            
            {isAuthenticated ? (
              <Button
                className="bg-blue-600 hover:bg-blue-700 text-white ml-2"
                onClick={navigateToApp}
              >
                Go to App
              </Button>
            ) : (
              <div className="flex items-center gap-2 ml-2">
                <Button
                  variant="outline" 
                  onClick={() => navigate("/login")}
                  className="flex items-center gap-1"
                >
                  <LogIn className="h-4 w-4" />
                  Sign In
                </Button>
                <Button
                  className="bg-blue-600 hover:bg-blue-700 text-white"
                  onClick={() => navigate("/signup")}
                >
                  <UserPlus className="h-4 w-4 mr-1" />
                  Sign Up
                </Button>
              </div>
            )}
          </nav>
          
          {/* Mobile Menu Button */}
          <Button
            variant="ghost"
            size="icon"
            className="md:hidden"
            onClick={() => setIsMenuOpen(!isMenuOpen)}
          >
            {isMenuOpen ? (
              <X className="h-6 w-6" />
            ) : (
              <Menu className="h-6 w-6" />
            )}
          </Button>
        </div>
      </div>
      
      {/* Mobile Menu */}
      {isMenuOpen && (
        <div className="md:hidden border-t border-gray-100 bg-white">
          <div className="container mx-auto px-4 py-2">
            <nav className="flex flex-col space-y-2">
              {menuItems.map(item => (
                <Button
                  key={item.name}
                  variant="ghost"
                  className={`justify-start ${isActive(item.path) ? "text-blue-600" : "text-gray-600"}`}
                  onClick={() => {
                    navigate(item.path);
                    setIsMenuOpen(false);
                  }}
                >
                  {item.name}
                </Button>
              ))}
              
              {isAuthenticated ? (
                <Button
                  className="bg-blue-600 hover:bg-blue-700 text-white w-full mt-2"
                  onClick={() => {
                    navigateToApp();
                    setIsMenuOpen(false);
                  }}
                >
                  Go to App
                </Button>
              ) : (
                <>
                  <Button
                    variant="outline"
                    className="w-full mt-2 justify-center"
                    onClick={() => {
                      navigate("/login");
                      setIsMenuOpen(false);
                    }}
                  >
                    <LogIn className="h-4 w-4 mr-1" />
                    Sign In
                  </Button>
                  <Button
                    className="bg-blue-600 hover:bg-blue-700 text-white w-full"
                    onClick={() => {
                      navigate("/signup");
                      setIsMenuOpen(false);
                    }}
                  >
                    <UserPlus className="h-4 w-4 mr-1" />
                    Sign Up
                  </Button>
                </>
              )}
            </nav>
          </div>
        </div>
      )}
    </header>
  );
};

export default WebsiteHeader;
